import Footer from '../home/components/Footer';

export default function Methodology() {
  const metrics = [
    {
      name: 'CrownScore',
      formula: 'Retention 40% + Task Success 30% + UX Severity 30%',
      scale: '0–100',
      description: 'Composite validation metric combining behavioral and qualitative signals',
      icon: 'ri-trophy-line'
    },
    {
      name: 'Task Success Rate',
      formula: '% of testers completing task correctly',
      scale: '0–100%',
      description: 'Measures usability and flow effectiveness',
      icon: 'ri-checkbox-circle-line'
    },
    {
      name: 'Retention Lift',
      formula: '% change in retention between rounds',
      scale: 'D1/D7',
      description: 'Tracks improvement in user engagement over validation cycles',
      icon: 'ri-line-chart-line'
    },
    {
      name: 'Crash Rate',
      formula: 'Incidents per 1,000 sessions',
      scale: 'Per 1K',
      description: 'Technical stability and reliability indicator',
      icon: 'ri-bug-line'
    },
    {
      name: 'Funnel Conversion',
      formula: '% moving between key steps',
      scale: 'Signup → Onboarding → Paid',
      description: 'Measures conversion effectiveness across critical user journeys',
      icon: 'ri-filter-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">Methodology &amp; Metrics</h1>
          <p className="text-xl lg:text-2xl text-indigo-100 max-w-3xl mx-auto">
            Scientific validation framework with statistical rigor and investor-grade reporting
          </p>
        </div>
      </div>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Metrics Definitions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Standardized, comparable metrics that translate user behavior into investment signals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {metrics.map((metric, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-200">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex-shrink-0">
                    <i className={`${metric.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{metric.name}</h3>
                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      Scale: {metric.scale}
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-mono text-gray-700">{metric.formula}</p>
                </div>
                <p className="text-gray-700">{metric.description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Sampling &amp; Statistical Power</h3>
              <p className="text-gray-700 mb-6">
                We recommend minimum sample sizes based on validation goals to ensure statistical significance and reliable insights.
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-gray-900">Functional Checks</strong>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">50 testers</span>
                  </div>
                  <p className="text-sm text-gray-600">Core flow validation and bug identification</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-gray-900">Retention &amp; Segmentation</strong>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">100–200 testers</span>
                  </div>
                  <p className="text-sm text-gray-600">Cohort analysis and behavioral patterns</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-gray-900">Market Validation</strong>
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">200–500+ testers</span>
                  </div>
                  <p className="text-sm text-gray-600">Distribution simulation and investor signals</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-100 border border-blue-300 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Statistical Rigor:</strong> We compute confidence intervals for behavioral metrics and annotate results with statistical significance levels.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Heatmap &amp; Session Logic</h3>
              <p className="text-gray-700 mb-6">
                Advanced behavioral analytics combining session replays, interaction patterns, and event telemetry to identify optimization opportunities.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-video-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Session Replays</strong>
                    <p className="text-sm text-gray-700">Full user journey recordings with interaction timeline</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-cursor-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Click Maps</strong>
                    <p className="text-sm text-gray-700">Aggregated click patterns and interaction hotspots</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-scroll-to-bottom-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Scroll Depth Analysis</strong>
                    <p className="text-sm text-gray-700">Content engagement and attention metrics</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <strong className="text-gray-900">Time-on-Task</strong>
                    <p className="text-sm text-gray-700">Task completion efficiency and friction points</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-purple-100 border border-purple-300 rounded-lg p-4">
                <p className="text-sm text-purple-900">
                  <strong>Drop-off Identification:</strong> Event telemetry combined with session data pinpoints exact moments where users abandon flows.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-bar-chart-box-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-3">Investor-Grade Reporting</h3>
                <p className="text-lg text-indigo-100 mb-4">
                  All metrics are presented with confidence intervals, statistical significance annotations, and comparative benchmarks. Reports include executive summaries, KPI dashboards, and evidence attachments suitable for due diligence.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-semibold">Executive Summary</span>
                  <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-semibold">KPI Dashboards</span>
                  <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-semibold">Evidence Attachments</span>
                  <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-semibold">Comparative Benchmarks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
